// src/components/portal/PortalLayout.js
import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { 
    FileText,       // Quotes
    CheckSquare,    // Requests
    Calendar,       // Appointments
    CreditCard,     // Invoices
    Phone,          // Contact
    LogOut,         // Logout
    Menu,           // Mobile Menu
    X,              // Close Menu
    Plus            // New Request Icon
} from 'lucide-react';
import logo from '../../assets/logo.jpg'; // <--- Ensure this file exists in src/assets/
import { resolveDocTerminology } from '../../utils/quoteSchema'; // r1-p3-docterm-portalnav-5b8e2f41

const PortalLayout = ({ user, organization, onLogout }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // r1-p3-docterm-portalnav-5b8e2f41 — Quote→Estimate Phase 3 missed this nav.
    // The sidebar is an AGGREGATE surface (it isn't showing one document), so it
    // resolves from the org default, not from any quote's docType — same rule the
    // operator sidebar follows. o3-portalproj-9c4e7b21 — `organization` is no longer
    // the whole org doc: CustomerPortal now spreads in the getPublicOrganizationInfo
    // projection, which serves quoteTerminologyDefault, so this still needs no extra
    // read. An absent value falls back to Quotes.
    const { wordPlural: quotesNavLabel } = resolveDocTerminology(organization?.quoteTerminologyDefault);

    // New Request Button (Jobber Style)
    const NewRequestButton = () => (
        <NavLink 
            to="/portal/requests/new" 
            className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-md mb-6 transition-colors shadow-sm"
            onClick={() => setIsMobileMenuOpen(false)}
        >
            <Plus size={18} />
            <span>New Request</span>
        </NavLink>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* --- SIDEBAR (Desktop) --- */}
            <aside className="hidden md:flex w-64 bg-white border-r border-gray-200 flex-col fixed h-full z-10">
                
                {/* --- LOGO AREA --- */}
                <div className="p-6 border-b border-gray-100 flex flex-col items-center justify-center min-h-[220px]">
                    {/* Logo Container */}
                    <div className="w-28 h-28 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center p-2 mb-4">
                        <img 
                            src={logo} 
                            alt="Spatchy Logo" 
                            className="w-full h-full object-contain rounded-lg" 
                        />
                    </div>
                    
                    {/* Organization Name (e.g. Luft's Landscaping) */}
                    <h1 className="text-lg font-bold text-gray-800 tracking-tight text-center leading-tight">
                        {organization?.companyName || 'Welcome'} 
                    </h1>
                    <span className="text-gray-400 text-xs font-medium uppercase tracking-widest mt-2">Client Portal</span>
                </div>

                {/* Navigation */}
                <div className="flex-1 p-4 overflow-y-auto">
                    <NewRequestButton />
                    
                    <nav className="space-y-1">
                        <PortalNavItem to="/portal/requests" icon={<CheckSquare size={20} />} label="Requests" />
                        <PortalNavItem to="/portal/quotes" icon={<FileText size={20} />} label={quotesNavLabel} />{/* r1-p3-docterm-portalnav-5b8e2f41 */}
                        <PortalNavItem to="/portal/appointments" icon={<Calendar size={20} />} label="Appointments" />
                        <PortalNavItem to="/portal/invoices" icon={<CreditCard size={20} />} label="Invoices" />
                        <PortalNavItem to="/portal/contact" icon={<Phone size={20} />} label="Contact Us" />
                    </nav>
                </div>

                {/* Logout */}
                <div className="p-4 border-t border-gray-100">
                    <button 
                        onClick={onLogout}
                        className="flex items-center gap-3 text-gray-500 hover:text-red-600 px-4 py-2 w-full transition-colors rounded-md hover:bg-red-50"
                    >
                        <LogOut size={20} />
                        <span className="font-medium">Log Out</span>
                    </button>
                </div>
            </aside>

            {/* --- MOBILE HEADER --- */}
            <div className="md:hidden fixed top-0 w-full bg-white border-b border-gray-200 z-20 px-4 h-16 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className="h-10 w-10 object-contain rounded-md"
                    />
                    <h1 className="text-lg font-bold text-gray-800">
                        {organization?.companyName || 'Spatchy'}
                    </h1>
                </div>
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-gray-600">
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* --- MOBILE MENU OVERLAY --- */}
            {isMobileMenuOpen && (
                <div className="md:hidden fixed inset-0 bg-gray-800 bg-opacity-50 z-10" onClick={() => setIsMobileMenuOpen(false)}>
                    <div className="bg-white w-64 h-full pt-20 p-4" onClick={e => e.stopPropagation()}>
                        <NewRequestButton />
                        <nav className="space-y-2">
                            <PortalNavItem to="/portal/requests" icon={<CheckSquare size={20} />} label="Requests" onClick={() => setIsMobileMenuOpen(false)} />
                            <PortalNavItem to="/portal/quotes" icon={<FileText size={20} />} label={quotesNavLabel} onClick={() => setIsMobileMenuOpen(false)} />{/* r1-p3-docterm-portalnav-5b8e2f41 */}
                            <PortalNavItem to="/portal/appointments" icon={<Calendar size={20} />} label="Appointments" onClick={() => setIsMobileMenuOpen(false)} />
                            <PortalNavItem to="/portal/invoices" icon={<CreditCard size={20} />} label="Invoices" onClick={() => setIsMobileMenuOpen(false)} />
                            <PortalNavItem to="/portal/contact" icon={<Phone size={20} />} label="Contact Us" onClick={() => setIsMobileMenuOpen(false)} />
                            <button onClick={onLogout} className="flex items-center gap-3 text-gray-500 hover:text-red-600 px-4 py-3 w-full mt-4 border-t pt-4">
                                <LogOut size={20} />
                                <span>Log Out</span>
                            </button>
                        </nav>
                    </div>
                </div>
            )}

            {/* --- MAIN CONTENT AREA --- */}
            <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8 bg-gray-50 min-h-screen">
                <div className="max-w-5xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

// Helper Component for Nav Links
const PortalNavItem = ({ to, icon, label, onClick }) => (
    <NavLink 
        to={to} 
        onClick={onClick}
        className={({ isActive }) => 
            `flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-medium ${
                isActive 
                ? 'bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-200' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`
        }
    >
        {icon}
        <span>{label}</span>
    </NavLink>
);

export default PortalLayout;